// ==================================================
// IMPORTS
// ==================================================
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Check, ChevronDown } from "lucide-react";
import "./KuddleSelect.css";

// ==================================================
// KUDDLE SELECT COMPONENT
//
// Purpose:
// Reusable custom dropdown used across Kuddlecore.
//
// Examples:
// Audience Select
// Priority Select
// Time Select
// Tag Select
//
// ==================================================

const KuddleSelect = ({
    label,
    value,
    onChange,
    options = [],
    className = "",
    placeholder = "Select",
    disabled = false,
    searchable = false,
    menuPlacement = "bottom",
}) => {

    // ==================================================
    // STATE & REFS
    // ==================================================
    const [open, setOpen] = useState(false);
    const [searchText, setSearchText] = useState("");
    const wrapRef = useRef(null);

    // ==================================================
    // SELECTED OPTION
    // ==================================================

    const selectedOption = options.find((option) => option.value === value);

    // ==================================================
    // FILTERED SEARCH OPTIONS
    // ==================================================

    const filteredOptions = useMemo(() => {
        if (!searchable || !searchText.trim()) return options;

        const q = searchText.trim().toLowerCase();

        return options.filter((option) =>
            option.label.toLowerCase().includes(q)
        );
    }, [options, searchable, searchText]);

    // ==================================================
    // OUTSIDE CLICK HANDLER
    // ==================================================
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (wrapRef.current && !wrapRef.current.contains(event.target)) {
                setOpen(false);
                setSearchText("");
            }
        };

        document.addEventListener("mousedown", handleClickOutside);

        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, []);

    // ==================================================
    // OPTION SELECTION HANDLER
    // ==================================================

    const handleSelect = (option) => {
        if (disabled) return;

        onChange(option.value);
        setOpen(false);
        setSearchText("");
    };

    // ==================================================
    // RENDER UI
    // ==================================================

    return (
        <div
            className={`kuddle-select-wrap ${className}`}
            ref={wrapRef}
        >
            {label && (
                <label className="kuddle-select-label">
                    {label}
                </label>
            )}

            <button
                type="button"
                className={`kuddle-select-btn ${open ? "open" : ""}`}
                onClick={() => {
                    if (!disabled) setOpen((prev) => !prev);
                }}
                disabled={disabled}
                aria-haspopup="listbox"
                aria-expanded={open}
            >
                <span className={!selectedOption ? "kuddle-select-placeholder" : ""}>
                    {selectedOption?.label || placeholder}
                </span>

                <ChevronDown
                    size={18}
                    className={`kuddle-select-chevron ${open ? "open" : ""}`}
                />
            </button>

            {open && (
                <div
                    className={`kuddle-select-menu ${menuPlacement === "top" ? "open-up" : "open-down"}`}
                    role="listbox"
                >
                    {searchable && (
                        <div className="kuddle-select-search-wrap">
                            <input
                                className="kuddle-select-search"
                                value={searchText}
                                onChange={(e) => setSearchText(e.target.value)}
                                placeholder="Search..."
                                autoFocus
                            />
                        </div>
                    )}

                    {filteredOptions.length > 0 ? (
                        filteredOptions.map((option) => (
                            <button
                                key={option.value}
                                type="button"
                                role="option"
                                aria-selected={option.value === value}
                                className={`kuddle-select-option ${option.value === value ? "selected" : ""
                                    }`}
                                onClick={() => handleSelect(option)}
                            >
                                <span>{option.label}</span>

                                {option.value === value && (
                                    <Check size={16} className="kuddle-select-check" />
                                )}
                            </button>
                        ))
                    ) : (
                        <div className="kuddle-select-empty">
                            No options found
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default KuddleSelect;